import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { AppConfigs, AppCriteria, AppSkinProps, CriteriaConstraint } from '../../../types/decisions';
import BasicTabs from '../../tabs/tabs';
import { getFieldName, scrollToAnchor, transformCriteriaData } from '../../../utils';
import { TriangleSlider, SliderV2 } from '../../slider';
import CriteriaSetting from './CriteriaSetting';
import CriteriaSettings from './CriteriaSettings';
import Modal from '../../modal/Modal';
import { useAppConfig } from '../../../context/app-configs-context';
import NameWithIcon from './NameWithIcon/NameWithIcon';
import GuidedTour from '../../GuidedTour/GuidedTour';
import NotificationSummary from './NotificationSummary/NotificationSummary';
import { trackActivity } from '../../../api/utils.api';
import { sumActivity } from '../../../api/decisions.api';

export interface DecisionConfig {
    decideButtonName?: string;
    notifyButtonName?: string;
    shouldRankOnDecideButtonClick?: boolean;
    showNotifyBytton?: boolean;
    showNotifyButton?: boolean;
    [key: string]: unknown;
}

export interface CriteriaImportanceAndSettingsProps {
    criteriaFields: AppSkinProps['criteriaData']['fields'];
    onSliderChange: AppSkinProps['onSliderChange'];
    fieldInitialValues: AppSkinProps['fieldInitialValues'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange'];
    onResetTradeOffs: AppSkinProps['onResetTradeOffs'];
    onCriteriaApply: AppSkinProps['onCriteriaApply'];
    onNotifyApply?: AppSkinProps['onNotifyApply'];
    externalItems?: AppConfigs['show']['menuItems']['additionalItems'];
    renderCustomSettings?: boolean;
    decisionConfig?: DecisionConfig;
    appId?: string | number;
    shouldUseRegularSlider?: boolean;
    criteria: AppCriteria,
    constraints: { [key: string]: CriteriaConstraint[]; }
}

export const steps = [
    {
        id: 'app-criteria-fields',
        title: 'Set What Matters Most',
        description: 'Slide right the bars of the most important decision criteria and left for less important ones, to express your needs and priorities.',
    },
    {
        id: 'decide-btn',
        title: 'Get Your Recommendation',
        description: 'Click Decide to see personalized recommendation, tailored to your tradeoffs',
    },
];

export const AUTO_SAVE_DELAY = 500;

const CriteriaImportanceAndSettings = ({
    criteriaFields,
    onSliderChange,
    fieldInitialValues,
    onTradeoffValueChange,
    onResetTradeOffs,
    onCriteriaApply,
    onNotifyApply,
    renderCustomSettings,
    externalItems = [],
    decisionConfig,
    appId,
    criteria,
    constraints,
    shouldUseRegularSlider = false,
}: CriteriaImportanceAndSettingsProps): React.JSX.Element => {
    const location = useLocation();
    const isDisabled = location.pathname.startsWith('/patient-choice');
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';

    const { appConfigs: { criteriaSettings: { popup } } } = useAppConfig();

    const [ isImportanceOpen, setIsImportanceOpen ] = useState(true);
    const [ open, setOpen ] = useState(false);
    const [ modalContent, setModalContent ] = useState<React.ReactElement>(<></>);
    const [ activeTab, setActiveTab ] = useState(0);

    const criteriaChangedOnceRef = useRef<{
        [key: string]: boolean;
    }>({});

    const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onCriteriaApplyRef = useRef(onCriteriaApply);
    const datasetRef = useRef(dataset);

    const decideButtonName = decisionConfig?.decideButtonName || 'Decide';
    const notifyButtonName = decisionConfig?.notifyButtonName || 'Notify';

    const shouldRankOnDecideButtonClick = decisionConfig?.shouldRankOnDecideButtonClick ?? true;

    const shouldShowNotifyButton =
        decisionConfig?.showNotifyButton ??
        decisionConfig?.showNotifyBytton ??
        false;

    useEffect(() => {
        onCriteriaApplyRef.current = onCriteriaApply;
    }, [ onCriteriaApply ]);

    useEffect(() => {
        datasetRef.current = dataset;
    }, [ dataset ]);

    const clearAutoSaveTimeout = () => {
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
            autoSaveTimeoutRef.current = null;
        }
    };

    const scheduleAutoCriteriaApply = () => {
        if (shouldRankOnDecideButtonClick || isDisabled) {
            return;
        }

        clearAutoSaveTimeout();

        autoSaveTimeoutRef.current = setTimeout(() => {
            onCriteriaApplyRef.current(datasetRef.current);
            autoSaveTimeoutRef.current = null;
        }, AUTO_SAVE_DELAY);
    };

    useEffect(() => {
        return () => {
            clearAutoSaveTimeout();
        };
    }, []);

    const handleSliderChange: AppSkinProps['onSliderChange'] = (...args) => {
        onSliderChange(...args);
        scheduleAutoCriteriaApply();
    };

    const handleTradeoffValueChange: AppSkinProps['onTradeoffValueChange'] = (...args) => {
        onTradeoffValueChange(...args);
        scheduleAutoCriteriaApply();
    };

    const handleResetTradeOffs = () => {
        onResetTradeOffs();
        scheduleAutoCriteriaApply();
    };

    const handleNotifyClick = async () => {
        if (!onNotifyApply) {
            return;
        }

        setModalContent(
            <Box sx={{ padding: '32px', minWidth: { xs: '280px', sm: '440px' }, textAlign: 'center' }}>
                <CircularProgress size={34}/>
                <Typography sx={{ marginTop: '16px', color: '#667085' }}>
                    Preparing summary...
                </Typography>
            </Box>,
        );

        setOpen(true);

        try {
            const sumActivityResponse = await sumActivity(
                transformCriteriaData(criteria, constraints),
                dataset,
            );

            const summary = sumActivityResponse?.summary?.trim() || 'No activity has been recorded yet.';
            setModalContent(<NotificationSummary summary={summary} handleClose={handleClose}/>);
            onNotifyApply(dataset);
        } catch (error) {
            console.error('SUM ACTIVITY ERROR:', error);

            setModalContent(
                <Box sx={{ padding: '32px', minWidth: { xs: '280px', sm: '440px' } }}>
                    <Typography variant="h5" sx={{ marginBottom: '12px', fontWeight: 700 }}>
                        Summary
                    </Typography>

                    <Typography sx={{ color: '#d92d20' }}>
                        Could not load the summary. Please try again.
                    </Typography>
                </Box>,
            );
        }
    };
    const handleExternalLinkClick = (label: string, link: string) => {
        if (appId) {
            trackActivity({
                app_id: appId,
                event: 'External Link',
                action: label,
                field: label,
                row_id: appId,
                referenced_row_id: appId,
                url: link,
                link: link,
            }).catch(console.error);
        }

        window.open(link, '_blank', 'noopener,noreferrer');
    };

    const handleClose = () => {
        setOpen(false);
        setModalContent(<></>);
    };

    const openCriteriaSettings = (id: string) => {
        const field = criteriaFields.find(item => item.id === id);

        if (!field || field.hide_field) {
            return;
        }

        setModalContent(
            <CriteriaSetting
                field={field}
                fieldInitialValues={fieldInitialValues}
                onTradeoffValueChange={handleTradeoffValueChange}
                onSliderChange={handleSliderChange}
                handleModalClose={handleClose}
                areCTAsDisplayed
                displayFullWidth
                disabled={isDisabled}
                renderCustomSettings={renderCustomSettings}
            />,
        );

        setOpen(true);
    };

    const onAutoPopupCheck = (newImportance: number, id: string) => {
        if (!popup.show || newImportance < popup.threshold) {
            return;
        }

        if (!criteriaChangedOnceRef.current[id]) {
            criteriaChangedOnceRef.current[id] = true;
            openCriteriaSettings(id);
        }
    };

    const handleImportanceChangeCommitted = (newImportance: number, id: string) => {
        onAutoPopupCheck(newImportance, id);
        scheduleAutoCriteriaApply();
    };

    let SliderComponent = TriangleSlider;
    if (shouldUseRegularSlider) {
        SliderComponent = SliderV2;
    }

    return (
        <div>
            <BasicTabs
                tabNames={[ isImportanceOpen ? 'Importance' : 'Settings' ]}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                tabs={[
                    <div className="app-criteria-fields" id="app-criteria-fields">
                        {isImportanceOpen ? criteriaFields.map(({
                            id,
                            name,
                            hide_importance,
                            hide_field,
                            icon,
                            link,
                        }, idx) => {
                            const criteriaName = getFieldName(name);

                            return (
                                <div key={id} className={`app-criteria-field ${hide_importance ? 'hidden' : ''}`}>
                                    <label>
                                        <NameWithIcon icon={icon} name={criteriaName} link={link}/>

                                        <SliderComponent
                                            name={id}
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={+criteriaFields[idx].importance}
                                            onChange={(_, value) => handleSliderChange(value as number, id)}
                                            onChangeCommitted={(_, value) => handleImportanceChangeCommitted(value as number, id)}
                                            isEditable
                                            isEditingDisabled={hide_field}
                                            onEditIconClick={() => openCriteriaSettings(id)}
                                            disabled={isDisabled}
                                        />
                                    </label>
                                </div>
                            );
                        }) : (
                            <CriteriaSettings
                                criteriaFields={criteriaFields}
                                onSliderChange={handleSliderChange}
                                onTradeoffValueChange={handleTradeoffValueChange}
                                fieldInitialValues={fieldInitialValues}
                                handleClose={handleClose}
                                openCriteriaSettings={openCriteriaSettings}
                                disabled={isDisabled}
                            />
                        )}
                    </div>,
                ]}
            />

            <div className="app-criteria-actions" style={{ display: 'block' }}>
                <div>
                    <Button
                        className="app-criteria-actions-action"
                        type="submit"
                        variant="outlined"
                        onClick={(e) => {
                            e.preventDefault();
                            setIsImportanceOpen(isOpen => !isOpen);

                            if (!isImportanceOpen) {
                                scrollToAnchor('app-criteria');
                            }
                        }}
                    >
                        {isImportanceOpen ? 'Settings' : 'Importance'}
                    </Button>

                    <Button
                        className="app-criteria-actions-action"
                        variant="outlined"
                        type="button"
                        onClick={handleResetTradeOffs}
                        disabled={isDisabled}
                    >
                        Reset
                    </Button>
                </div>

                {(shouldRankOnDecideButtonClick || shouldShowNotifyButton) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {shouldRankOnDecideButtonClick && (
                            <Button
                                className="app-criteria-actions-action"
                                type="submit"
                                variant="contained"
                                onClick={() => onCriteriaApply(dataset)}
                                disabled={isDisabled}
                                id="decide-btn"
                            >
                                {decideButtonName.trim() || 'Decide'}
                            </Button>
                        )}

                        {shouldShowNotifyButton && (
                            <Button
                                className="app-criteria-actions-action"
                                type="button"
                                variant="contained"
                                onClick={handleNotifyClick}
                                disabled={isDisabled || !onNotifyApply}
                                id="notify-btn"
                            >
                                {notifyButtonName.trim() || 'Notify'}
                            </Button>
                        )}
                    </div>
                )}

                {externalItems?.length > 0 && (
                    <div className="additional-menu-items" style={{ marginTop: 32 }}>
                        <h3>External links</h3>

                        <div
                            className="additional-menu-items-content"
                            style={{
                                marginTop: 16,
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                            }}
                        >
                            {externalItems.map(item => (
                                <Button
                                    key={`${item.label}-${item.link}`}
                                    className="app-criteria-actions-action"
                                    type="button"
                                    variant="outlined"
                                    sx={{
                                        textTransform: 'none',
                                        width: '48%',
                                        mb: 1,
                                    }}
                                    onClick={() => handleExternalLinkClick(item.label, item.link)}
                                >
                                    {item.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <Modal isOpen={open} handleClose={handleClose}>
                {modalContent}
            </Modal>

            <GuidedTour
                storageKey="new-decision-settings-tour-v1"
                steps={steps}
            />
        </div>
    );
};

export default CriteriaImportanceAndSettings;